import { useEffect, useState, useRef } from "react";
import { CommonBonusDetails } from "../../models/responses/index";
import {
  checkAndActivateCashdropBonus,
  checkAndActivateFreeSpinBonus,
  retrieveActiveBonusDetails,
  retrieveFreeSpinBonusDetails,
  updateUsersFreeSpinCancellation,
  activateCashbackAndWinningBoost,
} from "../../services/bonus.service";
import CashdropBonus from "../BonusTypes/CashdropBonus";
import FreeSpinBonus from "../BonusTypes/FreeSpinBonus";
import { BonusConsts } from "../../models/constants/bonusConsts";
import localization from "../../../../config/localization";
import DepositBonus from "../BonusTypes/DepositBonus";
import CashbackWinningBoostBonus from "../BonusTypes/CashbackWininngBoost";
import userDeviceStore from "../../../../store/device.store";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";

export function ActiveBonus() {
  const [data, setData] = useState<CommonBonusDetails[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);
  const dispatch = useAppDispatch();

  useEffect(() => {
    init();

    timerRef.current = setInterval(() => {
      fetchAllActiveBonuses(false);
    }, 60000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  async function fetchAllActiveBonuses(showLoader: boolean) {
    if (showLoader) dispatch(changeLoaderState(true));
    try {
      await updateUsersFreeSpinCancellation();
      await checkAndActivateFreeSpinBonus(null, accessModeSource.sourceType);
      await checkAndActivateCashdropBonus(null, accessModeSource.sourceType);

      const [activeBonuses, freeSpinBonuses] = await Promise.all([
        retrieveActiveBonusDetails(accessModeSource.sourceType),
        retrieveFreeSpinBonusDetails(accessModeSource.sourceType),
      ]);
      const merged = [...(activeBonuses ?? []), ...(freeSpinBonuses ?? [])].sort(
        (a, b) =>
          new Date(a.bonusexpiredate).getTime() -
          new Date(b.bonusexpiredate).getTime(),
      );

      setData(merged);
      await activateCashbackAndWinningBoost(accessModeSource.sourceType);
    } finally {
      if (showLoader) dispatch(changeLoaderState(false));
    }
  }

  async function init() {
    await fetchAllActiveBonuses(true);
  }

  async function handleBonusClaimed() {
    await fetchAllActiveBonuses(true);
  }

  return (
    <div>
      {data.length === 0 ? (
        <span>{localization.no_active_bonus}</span>
      ) : (
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {data.map((item, index) => {
            const bonusCardKey = `${item.bonuswallettransactionid}-${item.bonusid}-${item.bonustype}-${index}`;
            if (item.bonustype === BonusConsts.CashdropBonus) {
              return (
                <div key={bonusCardKey}>
                  <CashdropBonus item={item} onClaimed={handleBonusClaimed} />
                </div>
              );
            }
            if (item.bonustype === BonusConsts.FreeSpinBonus) {
              return (
                <div key={bonusCardKey}>
                  <FreeSpinBonus item={item} onClaimed={handleBonusClaimed} />
                </div>
              );
            }
            if (item.bonustype === BonusConsts.DepositBonus) {
              return (
                <div key={bonusCardKey}>
                  <DepositBonus item={item} onCancel={handleBonusClaimed} />
                </div>
              );
            }
            if (
              item.bonustype === BonusConsts.CashbackBonus ||
              item.bonustype === BonusConsts.WinningBoostBonus
            ) {
              return (
                <div key={bonusCardKey}>
                  <CashbackWinningBoostBonus
                    item={item}
                    onClaimed={handleBonusClaimed}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}
